import { ClientContext } from "ClientProvider";
import { getTransactionByAnalysis } from "api/graphQL_dashboard";
import { useEffect, useState, useContext, useRef, useCallback } from "react";

/* ─── helpers ─────────────────────────────────────────────── */
function calcEntries(obj: Record<string, number>) {
  const total = Object.values(obj).reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  return Object.entries(obj)
    .filter(([, v]) => v !== 0)
    .map(([key, value]) => ({
      key,
      original: value,
      pct: Math.round((value / total) * 100),
    }));
}
function lighten(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + Math.round(a * 255));
  const g = Math.min(255, ((n >> 8) & 0xff) + Math.round(a * 255));
  const b = Math.min(255, (n & 0xff) + Math.round(a * 255));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}
function darken(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((n >> 16) & 0xff) - Math.round(a * 255));
  const g = Math.max(0, ((n >> 8) & 0xff) - Math.round(a * 255));
  const b = Math.max(0, (n & 0xff) - Math.round(a * 255));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

/* ─── per-key style config ───────────────────────────────── */
function getKeyConfig(key: string) {
  const k = key.toUpperCase();
  if (k.includes('APPROV')) return {
    cls: 'approve', color: '#00c896',
    iconColor: '#00a67c', iconBg: 'rgba(0,200,150,.12)',
    textColor: '#006b4f', barBg: 'rgba(0,200,150,.15)',
    badgeBg: 'linear-gradient(135deg,#00e5a8,#00b889)', badgeColor: '#003d2a',
  };
  if (k.includes('DECLIN')) return {
    cls: 'decline', color: '#ff4d6d',
    iconColor: '#d63448', iconBg: 'rgba(255,77,109,.12)',
    textColor: '#b01020', barBg: 'rgba(255,77,109,.15)',
    badgeBg: 'linear-gradient(135deg,#ff7a87,#e04055)', badgeColor: '#fff',
  };
  return {
    cls: 'neutral', color: '#6366f1',
    iconColor: '#4f46e5', iconBg: 'rgba(99,102,241,.12)',
    textColor: '#312e81', barBg: 'rgba(99,102,241,.15)',
    badgeBg: 'linear-gradient(135deg,#818cf8,#4f46e5)', badgeColor: '#fff',
  };
}

const FALLBACK_COLORS = ['#00c896', '#ff4d6d', '#6366f1', '#f59e0b', '#0891b2'];

/* ─── 3D canvas donut hook ──────────────────────────────── */
function use3DDonut(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  entries: { key: string; original: number; pct: number }[] | null,
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !entries || entries.length === 0) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const S = 200;
    canvas.width = S * dpr; canvas.height = S * dpr;
    canvas.style.width = S + 'px'; canvas.style.height = S + 'px';
    ctx.scale(dpr, dpr);

    const total = entries.reduce((s, e) => s + e.original, 0);
    const cx = S / 2, cy = S / 2, OR = 88, IR = 52, GAP = 0.045, DUR = 1300;
    const start = Date.now();
    let hovered = -1, raf: number;
    const COLORS = entries.map(e => getKeyConfig(e.key).color);

    function getSegs(prog: number) {
      let a = -Math.PI / 2;
      return entries!.map((e, i) => {
        const full = (e.original / total) * 2 * Math.PI;
        const dg = Math.min(GAP, full * 0.25);
        const sweep = Math.max((full - dg * 2) * Math.min(prog, 1), 0);
        const sa = a + dg, ea = sa + sweep;
        a += full;
        return { sa, ea, color: COLORS[i] };
      });
    }

    function draw(prog: number) {
      ctx.clearRect(0, 0, S, S);
      const ss = getSegs(prog);

      // Pass 1: extrusion depth
      ss.forEach(({ sa, ea, color }, i) => {
        if (ea <= sa) return;
        const isH = i === hovered;
        const oR = isH ? OR + 8 : OR, iR = isH ? IR - 4 : IR, EX = isH ? 7 : 5;
        const mid = (sa + ea) / 2;
        const px = isH ? Math.cos(mid) * 6 : 0, py = isH ? Math.sin(mid) * 6 : 0;
        for (let d = EX; d >= 1; d--) {
          ctx.save(); ctx.translate(px, py + d);
          ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
          ctx.fillStyle = darken(color, 0.55);
          ctx.globalAlpha = Math.min(0.05 + (EX - d) * 0.045, 0.3);
          ctx.fill(); ctx.restore();
        }
      });

      // Pass 2: main face + highlights
      ss.forEach(({ sa, ea, color }, i) => {
        if (ea <= sa) return;
        const isH = i === hovered;
        const oR = isH ? OR + 8 : OR, iR = isH ? IR - 4 : IR;
        const mid = (sa + ea) / 2;
        const px = isH ? Math.cos(mid) * 6 : 0, py = isH ? Math.sin(mid) * 6 : 0;
        ctx.save(); ctx.translate(px, py);

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
        const rg = ctx.createRadialGradient(cx, cy, iR * 0.55, cx, cy, oR);
        rg.addColorStop(0, lighten(color, 0.32)); rg.addColorStop(0.42, color); rg.addColorStop(1, darken(color, 0.25));
        ctx.fillStyle = rg; ctx.globalAlpha = 1; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, iR + (OR - IR) * 0.48, ea, sa, true); ctx.closePath();
        const sg = ctx.createRadialGradient(cx, cy - (OR - IR) * 0.45, 0, cx, cy, oR);
        sg.addColorStop(0, 'rgba(255,255,255,.6)'); sg.addColorStop(0.55, 'rgba(255,255,255,.12)'); sg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sg; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, oR, sa, ea); ctx.arc(cx, cy, oR - 3.5, ea, sa, true); ctx.closePath();
        ctx.fillStyle = lighten(color, 0.28); ctx.globalAlpha = 0.42; ctx.fill();

        ctx.beginPath(); ctx.arc(cx, cy, iR + 2, sa, ea); ctx.arc(cx, cy, iR, ea, sa, true); ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,.35)'; ctx.globalAlpha = 0.6; ctx.fill();

        ctx.restore();
      });

      // Center hole
      const hg = ctx.createRadialGradient(cx, cy, IR - 10, cx, cy, IR + 6);
      hg.addColorStop(0, 'rgba(0,0,0,0)'); hg.addColorStop(0.65, 'rgba(0,0,0,.05)'); hg.addColorStop(1, 'rgba(0,0,0,.16)');
      ctx.beginPath(); ctx.arc(cx, cy, IR + 6, 0, Math.PI * 2); ctx.fillStyle = hg; ctx.globalAlpha = 1; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, IR - 1, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(245,247,255,.97)'; ctx.fill();
      ctx.strokeStyle = 'rgba(99,102,241,.2)'; ctx.lineWidth = 2; ctx.stroke();
    }

    function animate() {
      const raw = Math.min((Date.now() - start) / DUR, 1);
      const ease = raw < 0.5 ? 4 * raw * raw * raw : 0.5 * Math.pow(2 * raw - 2, 3) + 1;
      draw(ease);
      if (raw < 1) raf = requestAnimationFrame(animate);
    }
    raf = requestAnimationFrame(animate);

    function onMove(e: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * (S / rect.width);
      const my = (e.clientY - rect.top) * (S / rect.height);
      const dx = mx - cx, dy = my - cy, dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < IR - 2 || dist > OR + 12) { if (hovered !== -1) { hovered = -1; draw(1); } return; }
      let a = Math.atan2(dy, dx); if (a < -Math.PI / 2) a += 2 * Math.PI;
      const ss = getSegs(1); let found = -1;
      ss.forEach(({ sa, ea }, i) => { let s = sa, en = ea; if (s > en) en += 2 * Math.PI; if (a >= s && a <= en) found = i; });
      if (found !== hovered) { hovered = found; draw(1); }
    }
    function onLeave() { hovered = -1; draw(1); }
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    return () => { cancelAnimationFrame(raf); canvas.removeEventListener('mousemove', onMove); canvas.removeEventListener('mouseleave', onLeave); };
  }, [entries]);
}

/* ─── component ─────────────────────────────────────────── */
const TransactionAnalysisPieChartCard = ({
  type, datesTop, timeFrameTop, selectOrg, selectClient, selectCard,
}: {
  type?: string; datesTop?: any; timeFrameTop?: any;
  selectOrg?: any; selectClient?: any; selectCard?: any;
}) => {
  const [entries, setEntries] = useState<ReturnType<typeof calcEntries>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { dashboardData } = useContext(ClientContext);
  const [dates, setDates] = useState<[Date, Date]>(datesTop);
  const [timeFrame, setTimeFrame] = useState<string>(timeFrameTop);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    let data: any = {
      time_frame: timeFrame,
      org_ids: selectOrg?.length > 0 ? selectOrg.map((m: any) => m?.id).toString() : '',
      client_ids: selectClient?.length > 0 ? selectClient.map((m: any) => m?.id).toString() : '',
      card_type: selectCard?.length > 0 ? selectCard.map((m: any) => m?.value).toString() : '',
    };
    if (dates) { data.start_date = dates[0].toISOString().substring(0, 10); data.end_date = dates[1].toISOString().substring(0, 10); }
    if (timeFrame !== 'custom') { delete data.start_date; delete data.end_date; }
    getTransactionByAnalysis(type, dashboardData, data)
      .then((d: Record<string, number>) => setEntries(calcEntries(d)))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [dashboardData, dates, timeFrame, selectOrg, selectClient, selectCard]);

  useEffect(() => { const t = setTimeout(fetchData, 1000); return () => clearTimeout(t); }, [fetchData]);
  useEffect(() => { setDates(datesTop); setTimeFrame(timeFrameTop); }, [datesTop, timeFrameTop]);

  use3DDonut(canvasRef, entries);
  const total = entries?.reduce((s, e) => s + e.original, 0) ?? 0;

  const ApproveIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  );
  const DeclineIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  );

  return (
    <div className="ta-card">
      <div className="ta-orb" />
      <div className="ta-shine" />

      {/* Header */}
      <div className="ta-hdr">
        <div className="ta-hl">
          <div className="ta-ico">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
            </svg>
          </div>
          <div>
            <h3 className="ta-title">Transaction Analysis</h3>
            <p className="ta-subtitle">Approved vs declined breakdown</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className="ta-pill">{total.toLocaleString()} total</div>
        )}
      </div>

      {/* Body */}
      <div className="ta-body">
        {isLoading ? (
          <div className="ta-state">
            <div className="ta-spinner" />
            <p className="ta-hint">Loading analysis…</p>
          </div>
        ) : entries && entries.length > 0 ? (
          <>
            <div className="ta-chart-col">
              <div className="ta-canvas-wrap">
                <canvas ref={canvasRef} style={{ cursor: 'crosshair', display: 'block' }} />
                {/* <div className="ta-center">
                  <span className="ta-center-num">{total.toLocaleString()}</span>
                  <span className="ta-center-lbl">Total</span>
                </div> */}
              </div>
            </div>

            <div className="ta-breakdown">
              {entries.map((e, i) => {
                const cfg = getKeyConfig(e.key);
                const isApprove = cfg.cls === 'approve';
                const isDecline = cfg.cls === 'decline';
                return (
                  <div key={i} className={`ta-bd-card ta-bd-card--${cfg.cls}`}>
                    <div className="ta-bd-top">
                      <div className="ta-bd-left">
                        <div className="ta-bd-icon" style={{ background: cfg.iconBg, color: cfg.iconColor }}>
                          {isApprove ? <ApproveIcon /> : isDecline ? <DeclineIcon /> : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          )}
                        </div>
                        <span className="ta-bd-key" style={{ color: cfg.textColor }}>{e.key}</span>
                      </div>
                      <div className="ta-bd-badge" style={{ background: cfg.badgeBg, color: cfg.badgeColor }}>
                        {e.pct}%
                      </div>
                    </div>
                    <div className="ta-bd-value" style={{ color: cfg.textColor }}>{e.original.toLocaleString()}</div>
                    <div className="ta-bd-bar-row">
                      <div className="ta-bd-bar-bg" style={{ background: cfg.barBg }}>
                        <div className="ta-bd-bar-fill" style={{ width: `${e.pct}%`, background: cfg.color }} />
                      </div>
                      <span className="ta-bd-pct" style={{ color: cfg.textColor }}>{e.pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="ta-state">
            <div className="ta-empty-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
              </svg>
            </div>
            <p className="ta-empty-title">No analysis data</p>
            <p className="ta-hint">Adjust filters or date range to see results</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        .ta-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          border-radius: 28px; overflow: hidden; display: flex; flex-direction: column;
          position: relative; height: 100%;
          background: linear-gradient(155deg,#f5f8ff 0%,#eef2ff 50%,#f8f9ff 100%);
          border: 1.5px solid #d4dbff;
        }

        .ta-orb {
          position: absolute; top: -60px; right: -60px;
          width: 200px; height: 200px; border-radius: 50%;
          pointer-events: none; opacity: .07;
          background: radial-gradient(circle,#6366f1,transparent);
        }
        .ta-shine {
          position: absolute; inset: 0;
          background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,.5) 50%,transparent 70%);
          animation: taShine 4.5s ease-in-out infinite;
          pointer-events: none; border-radius: 28px; z-index: 0;
        }
        @keyframes taShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        .ta-hdr {
          padding: 16px 20px 12px; display: flex; align-items: center;
          justify-content: space-between; gap: 10px; flex-wrap: wrap;
          border-bottom: 1px solid rgba(99,102,241,.1); position: relative; z-index: 2;
        }
        .ta-hl { display: flex; align-items: center; gap: 11px; }
        .ta-ico {
          width: 42px; height: 42px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          background: linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow: 0 6px 20px rgba(99,102,241,.4),inset 0 1.5px 0 rgba(255,255,255,.45);
        }
        .ta-title { font-size: 13px; font-weight: 800; color: #0d1b3e; letter-spacing: -.2px; line-height: 1.3; margin-bottom: 2px; }
        .ta-subtitle { font-size: 9.5px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; color: #6366f1; }
        .ta-pill {
          font-size: 11px; font-weight: 900; padding: 5px 13px; border-radius: 100px; white-space: nowrap;
          background: linear-gradient(135deg,#818cf8,#4f46e5); color: #fff;
          box-shadow: 0 4px 12px rgba(99,102,241,.35);
        }

        .ta-body { display: grid; grid-template-columns: 220px 1fr; gap: 0; padding: 12px 0 20px; position: relative; z-index: 2; align-items: center; }

        .ta-chart-col { display: flex; align-items: center; justify-content: center; padding: 10px 0 10px 14px; }
        .ta-canvas-wrap { position: relative; width: 200px; height: 200px; }
        .ta-canvas-wrap canvas { display: block; }
        .ta-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
        .ta-center-num { font-size: 30px; font-weight: 900; letter-spacing: -1.5px; line-height: 1; color: #1e1b4b; }
        .ta-center-lbl { font-size: 8.5px; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; margin-top: 4px; opacity: .4; color: #0d1b3e; }

        .ta-breakdown { padding: 12px 18px 4px 10px; display: flex; flex-direction: column; gap: 10px; }

        .ta-bd-card {
          border-radius: 18px; padding: 14px 16px; border: 1.5px solid;
          position: relative; overflow: hidden;
          transition: transform .15s, box-shadow .15s; cursor: default;
        }
        .ta-bd-card:hover { transform: translateX(4px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
        .ta-bd-card::before {
          content: ''; position: absolute; top: 0; right: 0;
          width: 80px; height: 80px; border-radius: 50%; opacity: .06;
          transform: translate(30px,-30px); pointer-events: none;
        }
        .ta-bd-card--approve { background: linear-gradient(135deg,#f0fff8,#e6fff4); border-color: #b2f5db; }
        .ta-bd-card--approve::before { background: #00c896; }
        .ta-bd-card--decline { background: linear-gradient(135deg,#fff5f5,#ffe8e8); border-color: #ffc5c5; }
        .ta-bd-card--decline::before { background: #ff4d6d; }
        .ta-bd-card--neutral { background: linear-gradient(135deg,#f5f7ff,#eef1ff); border-color: #c7d0f8; }
        .ta-bd-card--neutral::before { background: #6366f1; }

        .ta-bd-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
        .ta-bd-left { display: flex; align-items: center; gap: 8px; }
        .ta-bd-icon {
          width: 28px; height: 28px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .ta-bd-key { font-size: 11px; font-weight: 800; letter-spacing: .2px; }
        .ta-bd-badge {
          font-size: 10px; font-weight: 900; padding: 3px 9px;
          border-radius: 100px; white-space: nowrap;
        }
        .ta-bd-value { font-size: 24px; font-weight: 900; letter-spacing: -1px; line-height: 1; margin-bottom: 10px; }
        .ta-bd-bar-row { display: flex; align-items: center; gap: 8px; }
        .ta-bd-bar-bg { flex: 1; height: 6px; border-radius: 100px; overflow: hidden; }
        .ta-bd-bar-fill { height: 100%; border-radius: 100px; transition: width .8s cubic-bezier(.4,0,.2,1); }
        .ta-bd-pct { font-size: 11px; font-weight: 900; min-width: 30px; text-align: right; }

        .ta-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; min-height: 220px; padding: 32px 24px; grid-column: 1 / -1; }
        .ta-empty-icon { width: 52px; height: 52px; border-radius: 14px; background: rgba(255,255,255,.8); border: 1.5px solid rgba(99,102,241,.15); display: flex; align-items: center; justify-content: center; color: #a5b4fc; }
        .ta-empty-title { font-size: 13px; font-weight: 800; color: #475080; }
        .ta-hint { font-size: 11.5px; color: #8a96b8; font-weight: 500; text-align: center; max-width: 200px; line-height: 1.5; }
        .ta-spinner { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid #c7d0f8; border-top-color: #4f46e5; animation: taSpin .7s linear infinite; }
        @keyframes taSpin { to { transform: rotate(360deg); } }

        @media (max-width: 380px) {
          .ta-chart-col { display:none  }
          .ta-body {display:block}
        }
      `}</style>
    </div>
  );
};

export default TransactionAnalysisPieChartCard;